import { Subject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';

import { SapiService } from '../services/sapi.service';
import { s3 } from '@blue-billywig/fine-uploader/lib/s3';

import { AuthenticationService } from '../services/authentication.service';
import { LocationService } from '../services/location.service';
import { Mediaclip } from '../interfaces/mediaclip';
import { sampleTime } from 'rxjs/operators';
import { ImportItem } from '../interfaces/importItem';

export interface BbFile {
	referenceId?: string;
	uploadIdentifier?: string;
	cancelId?: number;
	thumbData?: {
		data: string;
		width: number;
		height: number;
	};
	name?: string;
	size?: number;
	type?: string;
	uploaded?: number;
	progress?: number;
	extraParams?: any;
	timeRemaining?: number;
	timeElapsed?: number;
	uploadSpeed?: number;
	submittedDate?: Date;
	startedDate?: Date;
	width?: number;
	height?: number;
	status?: 'pending' | 'started' | 'error' | 'completed' | 'cancelled';
	hidden?: boolean;
	lastProgressDate?: Date;
	lastProgressLoaded?: number;
	removeMediaclipOnCancel?: boolean;
	file?: File | Blob;
	mediaclip?: Mediaclip;
	customData?: { [prop: string]: any };
}

export interface ProcessItem {
	type: 'ProcessItem';
	sourceClip?: Mediaclip;
	targetClip?: Mediaclip;
	id?: string;
	progressPercentage?: string;
	status?: 'new' | 'queued' | 'processing' | 'finished';
	message?: any;
}

export interface AwsUpload {
	accessKey: string;
	acl: string;
	currentDate: string;
	endpoint: string;
	path: string;
	region: string;
	uploadIdentifier: string;
}

@Injectable()
export class UploadService {
	// Object with uploads indexed by uploadIdentifiers
	public fileReference: any = {};
	public completedObservable: Subject<BbFile>;
	public cancelledObservable: Subject<void>;
	public addedObservable: Subject<BbFile | ImportItem | Mediaclip>;
	public startedUploadIdentifierObservable: Subject<BbFile>;
	public startedObservable: Subject<BbFile>;
	public progressObservable: Subject<BbFile>;
	putRight = false;

	private partSize = (5 * 1024 * 1024); // small chunks = better performance
	private uploaders: any = {};

	private internalProgressObservable: Subject<any>;

	constructor(
		private sapiService: SapiService,
		private locationService: LocationService,
		private authenticationService: AuthenticationService) {
		this.completedObservable = new Subject<BbFile>();
		this.cancelledObservable = new Subject<void>();
		this.addedObservable = new Subject<BbFile>();
		this.internalProgressObservable = new Subject<any>();
		this.progressObservable = new Subject<BbFile>();
		this.startedUploadIdentifierObservable = new Subject<BbFile>();
		this.startedObservable = new Subject<BbFile>();
		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);

		this.internalProgressObservable.pipe(
			sampleTime(300)
		).subscribe((progress: any) => {
			this.onUploadProgress(progress);
		});
	}

	handleFile(file: File, mediaclip?: Mediaclip, awsOptions?: any, customData?: { [prop: string]: any }): Observable<BbFile> {
		let opts: any = {};

		if (awsOptions) {
			opts = awsOptions;
		}

		if (mediaclip) {
			opts.type = 'mediaclip';
			opts.mediaclipId = mediaclip.id;
		}

		this.sapiService.getUrl('/awsupload', opts).subscribe((data: AwsUpload) => {
			const bbFile: BbFile = {
				uploadIdentifier: data.uploadIdentifier,
				mediaclip
			};
			this.startedUploadIdentifierObservable.next(bbFile);
			const uploader = this.uploaders[data.uploadIdentifier] = new s3.FineUploaderBasic({
				request: {

					// for some reason, chunked uploads need different endpoints
					endpoint: 'https://' + (file.size > this.partSize ? data.endpoint.split('/').shift() : data.endpoint),
					clockDrift: new Date(data.currentDate).getTime() - Date.now(),
					accessKey: data.accessKey,
					params: {
						uploadIdentifier: data.uploadIdentifier,
						'Content-Disposition': 'attachment; filename=' + encodeURIComponent(file.name)
					}
				},
				maxConnections: 1,
				retry: {
					enableAuto: true
				},
				cors: { expected: true, sendCredentials: true },
				objectProperties: {
					acl: data.acl,
					region: data.region,
					bucket: data.endpoint.split('/').pop(),
					key: (fileIdForKey: number) => {
						const fileForKey = uploader.getFile(fileIdForKey) as any;
						const parts = fileForKey.name.split('.');

						// for some reason, chunked uploads need different keys
						if (fileForKey.size < this.partSize) {
							return data.path + '/' + data.uploadIdentifier + (parts.length > 1 ? '.' + parts.pop() : '');
						} else {
							return data.endpoint.split('/').pop() + '/' + data.path + '/' +
								data.uploadIdentifier + (parts.length > 1 ? '.' + parts.pop() : '');
						}
					}
				},
				chunking: {
					enabled: (file.size > this.partSize),
					concurrent: { enabled: true },
					partSize: this.partSize
				},
				resume: { enabled: true },
				callbacks: {

					onSubmit: (id: number, fileName: string) => {
						const qqFile = uploader.getFile(id);
						this.onFileUploadStarted(file, id, data, mediaclip);
						return this.onFileSubmit(qqFile, data);
					},

					onUpload: () => {
						this.onUpload(data);
					},

					onProgress: (id, fileName, uploadProgress, fileSize) => {
						this.internalProgressObservable.next({ awsUpload: data, id, fileName, uploadProgress, fileSize });
					},

					onComplete: (id: number) => {
						this.onUploadComplete(id, data, customData);
					}
				},
				signature: { version: 4, endpoint: this.locationService.server + '/sapi/awsupload' },
				debug: true // false
			} as any); // s3.FineUploaderBasic says it's configuration doesn't know the "retry" option, but it does seem to work.

			uploader.addFiles([file]);
		});
		return this.startedUploadIdentifierObservable;
	}

	// First callback after upload start
	onFileSubmit(file: any, awsUpload: AwsUpload) {
		const bbFile = this.fileReference[awsUpload.uploadIdentifier];
		if (file.hasOwnProperty('removeMediaclipOnCancel')) {
			bbFile.removeMediaclipOnCancel = file.removeMediaclipOnCancel;
		}
		let pending = 0;
		let pendingFile = null;
		// console.warn('onFileSubmit', 'bbFile', bbFile, ' file', file, awsUpload);
		for (const uploadId in this.fileReference) {
			if (this.fileReference[uploadId].status === 'pending') {
				pendingFile = this.fileReference[uploadId];
				pending++;
			}
		}

		this.addedObservable.next(bbFile);

		if (pending > 1) {
			return pendingFile; // wait until previous upload is done
		} else {
			return true;
		}
	}

	onUpload(awsUpload: AwsUpload) {
		const bbFile: BbFile = this.fileReference[awsUpload.uploadIdentifier];
		bbFile.lastProgressDate = new Date();
		bbFile.lastProgressLoaded = 0;
	}

	initBbFile(file: File | Blob, id: number, awsUpload: AwsUpload, mediaclip?: Mediaclip) {
		file = file as any;
		const bbFile: BbFile = {
			name: (file as any).name,
			size: file.size,
			type: file.type,
			status: 'pending',
			file,

			uploaded: 0,
			progress: 0,
			timeRemaining: 0,
			timeElapsed: 0,
			uploadSpeed: 0,

			submittedDate: new Date(),
			uploadIdentifier: awsUpload.uploadIdentifier,
			cancelId: id,
			mediaclip,
		};
		if (mediaclip) {
			bbFile.extraParams = bbFile.extraParams || {};
			bbFile.extraParams.mediaclipId = mediaclip.id;
			bbFile.extraParams.type = 'mediaclip';
		}
		return bbFile;
	}

	onFileUploadStarted(file: File, id: number, awsUpload: AwsUpload, mediaclip?: Mediaclip) {
		// console.warn('onFileUploadStarted file', file, 'awsUpload', awsUpload, 'this.fileReference', this.fileReference);
		const bbFile = this.fileReference[awsUpload.uploadIdentifier] = this.initBbFile(file, id, awsUpload, mediaclip);
		bbFile.status = 'started';
		bbFile.size = file.size;
		bbFile.startedDate = new Date();
		this.startedObservable.next(file);
	}

	onUploadProgress(progress: any) {
		if (this.fileReference.hasOwnProperty(progress.awsUpload.uploadIdentifier)) {
			const bbFile: BbFile = this.fileReference[progress.awsUpload.uploadIdentifier];
			bbFile.uploaded = progress.uploadProgress;
			bbFile.progress = Math.min(1, (progress.uploadProgress / progress.fileSize));

			const currentDate = new Date();
			bbFile.timeElapsed = (currentDate.getTime() - bbFile.submittedDate.getTime()) / 1000; // Seconds instead of milliseconds

			// can we even this out somehow?
			// Bytes / sec:
			bbFile.uploadSpeed = (bbFile.uploaded - bbFile.lastProgressLoaded) / ((Date.now() - bbFile.lastProgressDate.getTime()) / 1000);

			bbFile.timeRemaining = (bbFile.size - progress.uploadProgress) / bbFile.uploadSpeed;

			this.progressObservable.next(bbFile);
		}
	}

	cancelUpload(file: BbFile) {
		if (file) {
			const uploader: s3.FineUploaderBasic = this.uploaders[file.uploadIdentifier];
			delete this.uploaders[file.uploadIdentifier];
			uploader.cancel(file.cancelId);
			file.status = 'cancelled';
			if (typeof file.removeMediaclipOnCancel === 'undefined' || file.removeMediaclipOnCancel) {
				if (file.mediaclip && file.mediaclip.type && file.mediaclip.id) {
					this.sapiService.delete(file.mediaclip.type.toLowerCase(), file.mediaclip.id).subscribe();
				}
			}
			this.cancelledObservable.next();
		}
	}

	getMediatypeForFile(file: File) {
		let mediaType = 'document';
		if (file && file.type) {
			if (file.type.startsWith('image')) {
				mediaType = 'image';
			} else if (file.type.startsWith('video')) {
				mediaType = 'video';
			} else if (file.type.startsWith('audio')) {
				mediaType = 'audio';
			} else if (file.type.startsWith('font')) {
				mediaType = 'font';
			}
		} else if (file.name && (
			file.name.toLowerCase().endsWith('ttf') ||
			file.name.toLowerCase().endsWith('woff') ||
			file.name.toLowerCase().endsWith('woff2') ||
			file.name.toLowerCase().endsWith('otf')
		)) {
			mediaType = 'font';
		}
		return mediaType;
	}

	onUploadComplete(completedId: number, uploader: AwsUpload, customData?: { [prop: string]: any }) {
		const bbFile: BbFile = this.fileReference[uploader.uploadIdentifier];
		// console.warn('onUploadComplete bbFile', bbFile, 'uploader', uploader);
		bbFile.uploaded = bbFile.size;
		bbFile.progress = 1;
		bbFile.status = 'completed';
		bbFile.customData = customData;
		this.updateHubspotContactUploadDate();

		if (bbFile.file.type.match('image.*')) {
			const reader = new FileReader();
			// Read in the image file as a data URL.
			reader.readAsDataURL(bbFile.file);
			reader.onload = (event: any) => {
				if (event.target.readyState === FileReader.DONE) {
					const image = new Image();
					image.src = (reader.result as string);
					// after image, we also have height and width available
					image.onload = () => {
						bbFile.thumbData = {
							data: event.target.result,
							width: image.width,
							height: image.height
						};
						this.completedObservable.next(bbFile);
					};
				}
			};
			return;
		} else {
			this.completedObservable.next(bbFile);
		}
	}


	getCountByStatus(status: string) {
		let count = 0;
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			if ((!status || bbFile.status === status) && !bbFile.hidden) {
				count++;
			}
		});
		return count;
	}

	getVisibleCount() {
		let count = 0;
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			if (!bbFile.hidden) {
				count++;
			}
		});
		return count;
	}

	getTimeRemaining() {
		let total = 0;
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			if (!bbFile.hidden) {
				total += bbFile.timeRemaining;
			}
		});
		return total;
	}

	getTimeElapsed() {
		// let count = 0;
		let total = 0;
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			if (!bbFile.hidden) {
				total += bbFile.timeElapsed;
				// count++;
			}
		});
		return total || 0;
	}

	getAvgUploadSpeed() {
		let count = 0;
		let total = 0;
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			if ((bbFile.status === 'pending' || bbFile.status === 'completed') &&
				!bbFile.hidden) {
				total += bbFile.uploadSpeed;
				count++;
			}
		});
		return total / count;
	}

	getUploadSpeed() {
		let total = 0;
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			total += bbFile.uploadSpeed;
		});
		return total || 0;
	}

	getAveragePercentage() {
		let count = 0;
		let total = 0;
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			if (!bbFile.hidden && bbFile.progress < 1) {
				total += bbFile.progress;
				count++;
			}
		});
		return count === 0 ? 0 : total / count;
	}

	hideAll() {
		Object.keys(this.fileReference).forEach((fileKey: string) => {
			const bbFile: BbFile = this.fileReference[fileKey];
			bbFile.hidden = true;
		});
	}

	onProcess(processItem: ProcessItem) {
		this.fileReference['processitem#' + processItem.id] = processItem;
		this.addedObservable.next(processItem);
	}

	setProcessingStatus(message: any) {
		// message from websocket
		if (!message.clipId) {
			return;
		}
		const processitem: ProcessItem = this.fileReference['processitem#' + message.clipId];

		if (processitem) {
			switch (message.ev) {
				default:
					processitem.status = message.ev;
					break;

				case 'progress':
					processitem.progressPercentage = message.percentage;
					break;

				case 'started':
					processitem.status = 'processing';
					break;
			}

			if (message?.message?.code && message?.message?.code !== 'UNKNOWN_ERROR') {
				processitem.message = { code: message.message.code };
			}
			this.addedObservable.next(processitem);
		}
	}

	onImport(importItem: ImportItem) {
		importItem.status = 'new'; // ignore status = imported from API response
		this.fileReference['importitem#' + importItem.id] = importItem;
		this.addedObservable.next(importItem);
	}

	onExport(exportedItem: any) {
		this.fileReference['exportitem#' + exportedItem.id] = exportedItem;
		this.addedObservable.next(exportedItem);
	}

	onExportItemDeleted(exportedItem: Mediaclip) {
		if (this.fileReference['exportitem#' + exportedItem.id]) {
			delete this.fileReference['exportitem#' + exportedItem.id];
		}
	}

	setImportStatus(message: any) {
		if (message.ev === 'progress' && !message.updateSrc) {
			return; // these events won't lead to a replace source / ingest source
		}

		let importItem: ImportItem;
		if (!message.ImportItemId) {
			// ev importJobXXX useses message.Object.id for import ID
			importItem = this.fileReference['importitem#' + message.Object?.id];
		} else {
			// ev progress (mediaclip) uses message.ImportItemId for import ID
			importItem = this.fileReference['importitem#' + message.ImportItemId];
		}

		if (!importItem) {
			return;
		}

		// info about video available: add info
		if (message.ev === 'importJobFileDownloaded') {
			importItem.label = message.label;
			importItem.src = message.src;
			importItem.externalId = message.externalId;
		}

		// mediaclip has been created: add info
		if (message.ev === 'importJobClipCreated' && message.clipId) {
			importItem.usetype = message.usetype; // only passed on this .ev
			importItem.oid = message.clipId;
			importItem.status = 'new';
			importItem.progressPercentage = '0';
		}

		switch (message.ev) {
			case 'progress':
				importItem.progressPercentage = message.percentage.replace('%', '');
				importItem.status = 'running';
				break;

			case 'importSourceDownloaded': // import finished
				importItem.progressPercentage = '100';
				importItem.status = 'imported';
				break;
		}

		if (message?.message?.code && message?.message?.code !== 'UNKNOWN_ERROR') {
			importItem.message = { code: message.message.code };
		}

		this.addedObservable.next(importItem);
	}

	setExportStatus(message: any) {
		if (message.payload && message.payload.id) {
			if (message.ev === 'exportItemUploading' || message.ev === 'exportItemError' || message.ev === 'exportItemPublished') {
				const exportItem = this.fileReference['exportitem#' + message.payload.id];
				if (exportItem) {
					if (message.payload.YouTube_status) {
						exportItem.YouTube_status = message.payload.YouTube_status;
					};
					this.addedObservable.next(exportItem);
				}

				// FB lookup by exportQueue ID instead of mediaclip ID
				const exportObject = this.fileReference['exportitem#' + message.Object.id];
				if (exportObject) {
					switch (message.ev) {
						case 'exportItemUploading':
							exportObject.status = 'uploading';
							break;
						case 'exportItemPublished':
							exportObject.status = 'exported';
							break;
						case 'exportItemError':
							exportObject.status = 'error';
							break;
					}
					this.fileReference['exportitem#' + exportObject.id] = exportObject;
					this.addedObservable.next(exportItem);
				}
			}
		}
	}

	private updateHubspotContactUploadDate(): void {
		const params = { last_upload_date: new Date() };
		this.sapiService.postUrl('/user/me/updateHubspotContact', params).subscribe();
	}
}
